import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, ArrowRight, ShieldCheck, Clock, Mail, ExternalLink } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { DPassLogo } from '../components/DPassLogo';

function PassportIllustration() {
  return (
    <svg viewBox="0 0 480 360" fill="none" className="w-full max-w-md mx-auto">
      {/* Glow backdrop */}
      <ellipse cx="240" cy="200" rx="180" ry="130" fill="rgba(5,150,105,0.12)" />
      <ellipse cx="240" cy="200" rx="130" ry="90"  fill="rgba(5,150,105,0.08)" />

      {/* Back card (tilted) */}
      <g transform="rotate(10, 240, 180)" opacity="0.5">
        <rect x="90" y="95" width="300" height="190" rx="16" fill="#065f46" />
      </g>

      {/* Main passport card */}
      <g transform="rotate(-4, 240, 180)">
        <rect x="70" y="80" width="300" height="195" rx="16" fill="#0f172a" />
        <rect x="70" y="80" width="300" height="195" rx="16" stroke="rgba(255,255,255,0.07)" strokeWidth="1" />

        {/* EU stars header strip */}
        <rect x="70" y="80"  width="300" height="28" rx="16" fill="#059669" />
        <rect x="70" y="96"  width="300" height="12" fill="#059669" />
        <text x="220" y="98" textAnchor="middle" fill="rgba(253,224,71,0.85)" fontSize="10" fontFamily="serif">
          ★  ★  ★  EU DIGITAL PRODUCT PASSPORT  ★  ★  ★
        </text>

        {/* Gold chip */}
        <rect x="100" y="124" width="44" height="34" rx="5" fill="#d4a017" />
        <line x1="100" y1="133" x2="144" y2="133" stroke="#b8860b" strokeWidth="0.8" />
        <line x1="100" y1="141" x2="144" y2="141" stroke="#b8860b" strokeWidth="0.8" />
        <line x1="100" y1="149" x2="144" y2="149" stroke="#b8860b" strokeWidth="0.8" />
        <line x1="116" y1="124" x2="116" y2="158" stroke="#b8860b" strokeWidth="0.8" />
        <line x1="123" y1="124" x2="123" y2="158" stroke="#b8860b" strokeWidth="0.8" />

        {/* NFC rings */}
        <path d="M306 118 Q310 130 306 142" stroke="rgba(255,255,255,0.45)" strokeWidth="2" fill="none" strokeLinecap="round" />
        <path d="M313 114 Q319 130 313 146" stroke="rgba(255,255,255,0.3)"  strokeWidth="2" fill="none" strokeLinecap="round" />
        <path d="M320 110 Q328 130 320 150" stroke="rgba(255,255,255,0.18)" strokeWidth="2" fill="none" strokeLinecap="round" />

        {/* Product info lines */}
        <rect x="100" y="170" width="155" height="7" rx="3.5" fill="rgba(255,255,255,0.55)" />
        <rect x="100" y="183" width="110" height="5" rx="2.5" fill="rgba(255,255,255,0.3)" />
        <rect x="100" y="194" width="130" height="5" rx="2.5" fill="rgba(255,255,255,0.2)" />

        {/* QR code */}
        <rect x="285" y="155" width="64" height="64" rx="8" fill="white" />
        {/* QR blocks */}
        <rect x="291" y="161" width="18" height="18" rx="2" fill="#0f172a" />
        <rect x="294" y="164" width="12" height="12" rx="1" fill="white" />
        <rect x="313" y="161" width="8"  height="8"  rx="1" fill="#0f172a" />
        <rect x="323" y="161" width="8"  height="8"  rx="1" fill="#0f172a" />
        <rect x="313" y="172" width="8"  height="8"  rx="1" fill="#0f172a" />
        <rect x="291" y="181" width="8"  height="8"  rx="1" fill="#0f172a" />
        <rect x="291" y="191" width="18" height="18" rx="2" fill="#0f172a" />
        <rect x="294" y="194" width="12" height="12" rx="1" fill="white" />
        <rect x="313" y="183" width="8"  height="8"  rx="1" fill="#0f172a" />
        <rect x="323" y="181" width="8"  height="14" rx="1" fill="#0f172a" />
        <rect x="313" y="195" width="18" height="8"  rx="1" fill="#0f172a" />

        {/* Compliant badge */}
        <rect x="100" y="212" width="80" height="22" rx="11" fill="#059669" />
        <text x="140" y="227" textAnchor="middle" fill="white" fontSize="8.5" fontWeight="700">✓ COMPLIANT</text>

        {/* DPP ID */}
        <text x="220" y="258" textAnchor="middle" fill="rgba(255,255,255,0.25)" fontSize="7.5" fontFamily="monospace">
          dpp-0cdb8a5e-60b1-4145-9dc1-e4cb4f1aad59
        </text>
      </g>

      {/* CO₂ floating badge */}
      <g transform="translate(405, 100)">
        <circle r="36" fill="white" fillOpacity="0.95" cx="0" cy="0" />
        <circle r="36" stroke="#059669" strokeWidth="1.5" fill="none" cx="0" cy="0" />
        <text x="0" y="-9"  textAnchor="middle" fill="#059669"  fontSize="9"  fontWeight="700">CO₂</text>
        <text x="0" y="5"   textAnchor="middle" fill="#047857"  fontSize="14" fontWeight="900">Low</text>
        <text x="0" y="17"  textAnchor="middle" fill="#6ee7b7"  fontSize="7.5">emission</text>
      </g>

      {/* Recycling badge */}
      <g transform="translate(62, 90)">
        <circle r="30" fill="white" fillOpacity="0.95" cx="0" cy="0" />
        <circle r="30" stroke="#10b981" strokeWidth="1.5" fill="none" cx="0" cy="0" />
        <text x="0" y="8" textAnchor="middle" fill="#059669" fontSize="22">♻</text>
      </g>

      {/* Leaf flourish right */}
      <path d="M445 225 C455 200 478 210 470 233 C462 252 440 250 445 225 Z" fill="#059669" fillOpacity="0.35" />
      <path d="M460 240 C468 228 475 240 465 248 Z"                           fill="#10b981" fillOpacity="0.4" />
      {/* Leaf flourish left */}
      <path d="M22 215 C15 190 38 182 44 207 C48 225 30 232 22 215 Z"         fill="#10b981" fillOpacity="0.35" />

      {/* Dashed connection lines */}
      <line x1="90"  y1="90"  x2="105" y2="155" stroke="rgba(16,185,129,0.2)" strokeWidth="1" strokeDasharray="4,4" />
      <line x1="405" y1="100" x2="355" y2="150" stroke="rgba(16,185,129,0.2)" strokeWidth="1" strokeDasharray="4,4" />

      {/* Bottom dots */}
      <circle cx="200" cy="310" r="5" fill="rgba(16,185,129,0.4)" />
      <circle cx="220" cy="300" r="3" fill="rgba(16,185,129,0.3)" />
      <circle cx="180" cy="300" r="3" fill="rgba(16,185,129,0.3)" />
      <circle cx="240" cy="308" r="4" fill="rgba(16,185,129,0.25)" />
      <circle cx="160" cy="308" r="4" fill="rgba(16,185,129,0.2)" />
    </svg>
  );
}

const IS_DEMO = typeof window !== 'undefined' && window.location.hostname === 'demo.d-pass.eu';
const IS_SAAS = typeof window !== 'undefined' && window.location.hostname === 'dpp.d-pass.eu';

function CertificationNotice() {
  const { t } = useTranslation();
  return (
    <div className="w-full max-w-[420px]">
      <div className="flex items-center justify-center mb-8">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 border border-amber-200 text-amber-700 text-xs font-semibold tracking-wide uppercase">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
          {t('login.restricted.badge')}
        </span>
      </div>
      <div className="flex justify-center mb-6">
        <div className="relative">
          <div className="w-20 h-20 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center">
            <ShieldCheck className="w-9 h-9 text-slate-400" strokeWidth={1.5} />
          </div>
          <div className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-amber-100 border-2 border-white flex items-center justify-center">
            <Clock className="w-3 h-3 text-amber-600" />
          </div>
        </div>
      </div>
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-slate-900 leading-tight">{t('login.restricted.title')}</h1>
        <p className="text-slate-500 text-sm mt-2 leading-relaxed max-w-sm mx-auto">{t('login.restricted.desc')}</p>
      </div>
      <div className="card p-5 mb-5 shadow-sm">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800">{t('login.restricted.certTitle')}</p>
              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{t('login.restricted.certDesc')}</p>
            </div>
          </div>
          <div className="border-t border-slate-100" />
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <ExternalLink className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800">{t('login.restricted.tryDemo')}</p>
              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{t('login.restricted.tryDemoDesc')}</p>
              <a href="https://demo.d-pass.eu/login" className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700 mt-1.5 transition-colors">
                {t('login.restricted.openDemo')} <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </div>
      <a
        href="mailto:contact@d-pass.eu?subject=D-Pass Platform Access Request"
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:border-slate-300 hover:bg-slate-50 transition-colors"
      >
        <Mail className="w-4 h-4 text-slate-400" />
        {t('login.restricted.contactUs')}
      </a>
      <p className="text-center text-xs text-slate-400 mt-6">AI Group LTD — EU Digital Product Passport Platform</p>
    </div>
  );
}

export function Login() {
  const { t } = useTranslation();
  useEffect(() => { document.title = `${t('login.signIn')} · D-Pass`; });

  const navigate = useNavigate();
  const { login } = useAuth();

  const DEMO_ACCOUNTS = [
    { role: 'Admin',  email: 'admin@ecocell.com',  password: 'Demo1234!!', description: t('login.demo.roles.admin'),  color: 'emerald' },
    { role: 'Editor', email: 'editor@ecocell.com', password: 'Demo1234!!', description: t('login.demo.roles.editor'), color: 'blue' },
    { role: 'Viewer', email: 'viewer@ecocell.com', password: 'Demo1234!!', description: t('login.demo.roles.viewer'), color: 'slate' },
  ];
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('admin@ecocell.com');
  const [password, setPassword] = useState(IS_DEMO ? 'Demo1234!!' : 'password');
  const [tenantSlug, setTenantSlug] = useState('ecocell');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await login(email, password, tenantSlug);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel — illustration */}
      <div className="hidden lg:flex flex-col w-[54%] sidebar-gradient relative overflow-hidden">
        {/* Mesh glow */}
        <div className="absolute inset-0 bg-mesh pointer-events-none" />
        <div className="absolute top-0 left-0 w-full h-full opacity-20"
          style={{ background: 'radial-gradient(ellipse at 60% 40%, rgba(5,150,105,0.5) 0%, transparent 65%)' }}
        />

        {/* Brand header */}
        <div className="relative z-10 px-10 pt-10 flex items-center gap-3">
          <DPassLogo className="w-10 h-10" />
          <div>
            <p className="text-white font-bold text-xl tracking-tight leading-none">D-Pass</p>
            <p className="text-slate-400 text-xs mt-0.5">Digital Product Passports</p>
          </div>
        </div>

        {/* Illustration */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-10 -mt-8">
          <PassportIllustration />
          <div className="mt-8 text-center max-w-sm">
            <h2 className="text-white text-2xl font-bold leading-snug">
              {t('login.tagline').replace(',', ',\n').split('\n')[0]}<br />
              <span className="text-gradient">{t('login.tagline').replace(',', ',\n').split('\n')[1]}</span>
            </h2>
            <p className="text-slate-400 text-sm mt-3 leading-relaxed">{t('login.taglineDesc')}</p>
          </div>
        </div>

        {/* Feature pills */}
        <div className="relative z-10 px-10 pb-10 flex flex-wrap gap-2">
          {[t('login.features.batteryReg'), t('login.features.espr'), t('login.features.gdpr'), t('login.features.qr'), t('login.features.audit')].map((f) => (
            <span key={f} className="px-3 py-1.5 rounded-full bg-white/8 border border-white/10 text-slate-300 text-xs font-medium">
              {f}
            </span>
          ))}
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col items-center justify-center bg-slate-50 px-6 py-12">
        {/* Mobile logo */}
        <div className="lg:hidden mb-8 text-center">
          <div className="flex items-center justify-center gap-2.5 mb-3">
            <DPassLogo className="w-10 h-10" />
            <span className="text-slate-900 font-bold text-xl">D-Pass</span>
          </div>
        </div>

        {IS_SAAS && <CertificationNotice />}

        {!IS_SAAS && <div className="w-full max-w-[380px]">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-slate-900">{t('login.welcome')}</h1>
            <p className="text-slate-500 text-sm mt-1">{t('login.subtitle')}</p>
          </div>

          {IS_DEMO && (
            <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">{t('login.demo.title')}</span>
                <span className="text-xs text-emerald-600">— {t('login.demo.tenant').replace('ecocell', '')} <code className="font-mono bg-white px-1 py-0.5 rounded text-emerald-800">ecocell</code></span>
              </div>
              <div className="space-y-2">
                {DEMO_ACCOUNTS.map(account => (
                  <div
                    key={account.role}
                    className="flex items-center justify-between bg-white rounded-xl px-3 py-2 border border-emerald-100 cursor-pointer hover:border-emerald-300 transition-colors group"
                    onClick={() => {
                      setEmail(account.email);
                      setPassword(account.password);
                      setTenantSlug('ecocell');
                    }}
                    title={t('login.demo.clickToFill')}
                  >
                    <div className="flex items-center gap-2.5">
                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded-md ${
                        account.color === 'emerald' ? 'bg-emerald-100 text-emerald-700' :
                        account.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {account.role}
                      </span>
                      <div>
                        <p className="text-xs font-mono text-slate-700">{account.email}</p>
                        <p className="text-xs text-slate-400">{account.description}</p>
                      </div>
                    </div>
                    <span className="text-xs text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity font-medium">
                      {t('login.demo.use')}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-emerald-600 mt-2">{t('login.demo.passwordHint').split('Demo1234!!')[0]} <code className="font-mono bg-white px-1 py-0.5 rounded border border-emerald-200">Demo1234!!</code></p>
              <p className="text-xs text-slate-400 mt-1">{t('login.demo.resetNote')}</p>
            </div>
          )}

          <div className="card p-7 shadow-elevated">
            {error && (
              <div className="mb-5 p-3.5 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-start gap-2">
                <span className="mt-0.5 text-red-500 flex-shrink-0">⚠</span>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="label">{t('login.email')}</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                  autoComplete="email"
                  required
                />
              </div>

              <div>
                <label className="label">{t('login.password')}</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input pr-10"
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="label">{t('login.organization')}</label>
                <input
                  type="text"
                  value={tenantSlug}
                  onChange={(e) => setTenantSlug(e.target.value)}
                  className="input"
                  placeholder={t('login.orgPlaceholder')}
                  required
                />
              </div>

              <button type="submit" disabled={isLoading} className="w-full btn-primary py-2.5 text-sm justify-center mt-1">
                {isLoading ? t('login.signingIn') : (<>{t('login.signIn')} <ArrowRight className="w-4 h-4" /></>)}
              </button>
            </form>
          </div>

          <p className="text-center text-xs text-slate-400 mt-6">
            AI Group LTD — EU Digital Product Passport Platform
          </p>
        </div>}
      </div>
    </div>
  );
}
